// Global opportunities cache — fetched once on mount, shared via context.
//
// Why not React Query? Fursa's shape is tiny (< 20 rows total), a single
// fetch on boot is fine and avoids the dep bloat. If the dataset grows,
// swap the internals here — the hook signatures stay stable.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { bootstrap } from "@/services";
import { normalizeDigitsDeep } from "@/utils/normalizeDigits";

const OpportunitiesContext = createContext(null);

const isAbortError = (e) =>
  e && (
    e.name === "AbortError" ||
    e.code === 20 ||
    String(e).includes("aborted") ||
    // The Emergent preview iframe wrapper reflects an aborted fetch as
    // a plain `TypeError: Failed to fetch` on first hard-nav — treat it
    // as a benign retry-worthy signal, not a hard error.
    (e.name === "TypeError" && /failed to fetch/i.test(String(e.message)))
  );

export const OpportunitiesProvider = ({ children }) => {
  const [opportunities, setOpportunities]     = useState([]);
  const [featured, setFeatured]               = useState(null);
  const [countries, setCountries]             = useState([]);
  const [industries, setIndustries]           = useState([]);
  const [isLoading, setIsLoading]             = useState(true);
  const [error, setError]                     = useState(null);
  const controllerRef = useRef(null);

  const load = useCallback(async () => {
    // Cancel any in-flight fetch before starting a new one — kills the
    // noisy "TypeError: Failed to fetch" that comes from React 19
    // StrictMode double-invoking effects in dev.
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const raw = await bootstrap({ signal: controller.signal });
      if (controller.signal.aborted) return;
      const payload = normalizeDigitsDeep(raw);
      setOpportunities(payload.opportunities || []);
      setFeatured(payload.featured || null);
      setCountries(payload.countries || []);
      setIndustries(payload.industries || []);
    } catch (e) {
      if (isAbortError(e)) {
        // Swallow silent aborts; useEffect below auto-retries once.
        return;
      }
      // eslint-disable-next-line no-console
      console.error("OpportunitiesProvider bootstrap failed:", e);
      setError(e);
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  const value = useMemo(
    () => ({
      opportunities,
      featured,
      countries,
      industries,
      isLoading,
      error,
      refresh: load,
    }),
    [opportunities, featured, countries, industries, isLoading, error, load],
  );

  return (
    <OpportunitiesContext.Provider value={value}>
      {children}
    </OpportunitiesContext.Provider>
  );
};

const useCtx = () => {
  const ctx = useContext(OpportunitiesContext);
  if (!ctx) throw new Error("useOpportunities must be used inside OpportunitiesProvider");
  return ctx;
};

/* ─── Shared hooks ─────────────────────────────────────────────────── */

export const useOpportunities = () => useCtx();

/** Convenience — everything except the featured card. */
export const useLatestOpportunities = () => {
  const { opportunities, isLoading, error } = useCtx();
  const latest = useMemo(
    () => opportunities.filter((o) => !o.is_featured),
    [opportunities],
  );
  return { opportunities: latest, isLoading, error };
};
